import React from "react";

import _ from "lodash";

import { Loader } from "shared/components/LargeLoader";
import { ViewOnlyBanner } from "shared/components/ViewOnlyBanner";
import { useConfigLock } from "shared/hooks/useConfigLock";

import { Workflow, Workflows } from "shared/components/vega/WorkflowStudio";

import { useListApplicantWorkflows } from "shared/hooks/api";

import ApplicantWorkflowEditor from "./ApplicantWorkflowEditor";
import {
  useV1ExpressionMigration,
  getWorkflowType,
} from "./useV1ExpressionMigration";
import V1ExpressionMigration from "./V1ExpressionMigration";

interface ApplicantWorkflowsProps {
  workflows: any[];
  readOnlyWorkflows: any[];
  onRevertStaged: () => void;
  getStagedFormula: (workflowType: string) => string | undefined;
  shouldExpandWorkflow: (workflowType: string) => boolean;
}

const ApplicantWorkflows = ({
  workflows,
  readOnlyWorkflows = [],
  onRevertStaged,
  getStagedFormula,
  shouldExpandWorkflow,
}: ApplicantWorkflowsProps) => {
  const builder = (workflow: any, readOnly?: boolean) => {
    const workflowType = getWorkflowType(workflow?.identifier);

    // Get staged formula if applicable (only for editable, legacy workflows)
    const stagedFormula = !readOnly
      ? getStagedFormula(workflowType)
      : undefined;

    // Check if this workflow should be expanded
    const shouldExpand = !readOnly && shouldExpandWorkflow(workflowType);

    return (
      <Workflow
        workflow={workflow}
        readOnly={readOnly}
        key={workflow?.id}
        expand={shouldExpand}
      >
        <ApplicantWorkflowEditor
          workflow={workflow}
          canEdit={!readOnly}
          stagedFormula={stagedFormula}
          onRevertStaged={onRevertStaged}
        />
      </Workflow>
    );
  };

  // Ensure workflows is always an array
  const workflowsArray = Array.isArray(workflows) ? workflows : [];

  return (
    <Workflows
      workflows={workflowsArray}
      readOnlyWorkflows={readOnlyWorkflows}
      builder={builder}
    />
  );
};

interface ApplicantWorkflowStudioProps {
  location: string;
  readOnlyLocations?: string[];
  dealSetupId?: number;
  prescreeningDealSetupId?: number;
}

const ApplicantWorkflowStudio = ({
  location,
  readOnlyLocations = [],
  dealSetupId,
  prescreeningDealSetupId,
}: ApplicantWorkflowStudioProps) => {
  const { workflows, isLoading } = useListApplicantWorkflows(location, true);
  const { isLocked } = useConfigLock();

  // V1 Expression Migration - can be removed when migration is complete
  const {
    isStaged,
    handleStageExpressions,
    handleRevertStaged,
    handleLegacyWorkflowsDetected,
    getStagedFormula,
    shouldExpandWorkflow,
  } = useV1ExpressionMigration();

  const readOnlyWorkflowsFromLocations = _.chain(readOnlyLocations)
    .map((l) => {
      const { workflows } = useListApplicantWorkflows(l, true);
      return workflows || [];
    })
    .flatten()
    .uniqBy("id")
    .value();

  // Ensure workflows is always an array
  const workflowsArray = Array.isArray(workflows) ? workflows : [];

  // When config is locked, move all workflows to read-only
  const editableWorkflows = isLocked ? [] : workflowsArray;
  const readOnlyWorkflows = isLocked
    ? [...workflowsArray, ...readOnlyWorkflowsFromLocations]
    : readOnlyWorkflowsFromLocations;

  return (
    <div className="vega container">
      {isLocked && <ViewOnlyBanner />}
      <Loader loading={isLoading}>
        {/* V1 Expression Migration - can be removed when migration is complete */}
        {!isLocked && (dealSetupId || prescreeningDealSetupId) && (
          <V1ExpressionMigration
            dealSetupId={dealSetupId}
            prescreeningDealSetupId={prescreeningDealSetupId}
            location={location}
            onStageExpressions={handleStageExpressions}
            onRevert={handleRevertStaged}
            isStaged={isStaged}
            workflows={workflows}
            onLegacyWorkflowsDetected={handleLegacyWorkflowsDetected}
          />
        )}
        <ApplicantWorkflows
          workflows={editableWorkflows}
          readOnlyWorkflows={readOnlyWorkflows}
          onRevertStaged={handleRevertStaged}
          getStagedFormula={getStagedFormula}
          shouldExpandWorkflow={shouldExpandWorkflow}
        />
      </Loader>
    </div>
  );
};

export default ApplicantWorkflowStudio;
